import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    location: '',
    project_type: '',
    budget_range: '',
    message: '',
  });

  const londonPos = [51.5074, -0.0198]; // London coordinates
  const nyPos = [40.7128, -74.0080]; // NYC coordinates

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    // Form submits directly to Formspree
    // Show success message after submission
    toast.success('Quote request submitted!', {
      description: 'Our team will contact you within 24 hours.',
    });
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-semibold mb-4">Get in Touch</h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Ready to fortify your facility? Request a quote or speak with our security experts today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form & Map */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Form */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
                  <h2 className="text-2xl font-semibold mb-6">Request a Quote</h2>
                  <form
                    action="https://formspree.io/f/YOUR_FORM_ID"
                    method="POST"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    data-testid="contact-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="John Smith"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@company.com"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company *</Label>
                      <Input
                        id="company"
                        name="company"
                        placeholder="Your Company Name"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-company-input"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+1 234 567 8900"
                          value={formData.phone}
                          onChange={handleChange}
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location">Location *</Label>
                        <Input
                          id="location"
                          name="location"
                          placeholder="City, Country"
                          required
                          value={formData.location}
                          onChange={handleChange}
                          data-testid="contact-location-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="project_type">Project Type *</Label>
                        <Select
                          required
                          value={formData.project_type}
                          onValueChange={(value) => handleSelectChange('project_type', value)}
                        >
                          <SelectTrigger id="project_type" data-testid="contact-project-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="vault-doors">Vault Doors</SelectItem>
                            <SelectItem value="biometric-locks">Biometric Locks</SelectItem>
                            <SelectItem value="reinforced-doors">Reinforced Doors</SelectItem>
                            <SelectItem value="access-control">Access Control</SelectItem>
                            <SelectItem value="fire-resistant">Fire-Resistant Systems</SelectItem>
                            <SelectItem value="maintenance">Maintenance Services</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget_range">Budget Range</Label>
                        <Select
                          value={formData.budget_range}
                          onValueChange={(value) => handleSelectChange('budget_range', value)}
                        >
                          <SelectTrigger id="budget_range" data-testid="contact-budget-select">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-50k">Under $50,000</SelectItem>
                            <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                            <SelectItem value="100k-250k">$100,000 - $250,000</SelectItem>
                            <SelectItem value="250k-500k">$250,000 - $500,000</SelectItem>
                            <SelectItem value="over-500k">Over $500,000</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Project Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows="5"
                        placeholder="Describe your security requirements, facility type, and timeline..."
                        required
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="contact-message-textarea"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90"
                      data-testid="contact-submit-button"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send Inquiry
                    </Button>
                  </form>
                </div>
              </motion.div>
            </div>

            {/* Office Info & Map */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6"
              >
                {/* Office Cards */}
                <div className="space-y-4">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-accent" />
                      London Office
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>14 Canary Wharf</p>
                      <p>London, UK</p>
                      <div className="pt-2 space-y-1">
                        <p className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-accent" />
                          +44 7494 695344
                        </p>
                        <p className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-accent" />
                          london@greenfluxsystems.org
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-accent" />
                      New York Office
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>280 Hudson Street</p>
                      <p>New York, NY, USA</p>
                      <div className="pt-2 space-y-1">
                        <p className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-accent" />
                          +1 646 391 5932
                        </p>
                        <p className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-accent" />
                          newyork@greenfluxsystems.org
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="office-map">
                  <div className="h-[400px]">
                    <MapContainer
                      center={[40, -20]}
                      zoom={2}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={londonPos}>
                        <Popup>
                          <strong>London Office</strong><br />
                          14 Canary Wharf, London, UK
                        </Popup>
                      </Marker>
                      <Marker position={nyPos}>
                        <Popup>
                          <strong>New York Office</strong><br />
                          280 Hudson Street, NY, USA
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
